import React from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18nService from 'src/services/i18n.service';
import { dialogContent, dialogTitle, modal } from 'src/components/ConfirmationModal/ConfirmationModal.styles';

import { Dialog, DialogTitle, DialogContent } from '@material-ui/core';
import { TextField } from '@material-ui/core';
import { createId } from '@paralleldrive/cuid2';
import { BasicItem } from 'src/types/Scope';
import SquareActionButton from 'src/common-ui/components/Buttons/SquareActionButton';

export enum SaveType {
  favorite,
}

/*
  Similar to the delete modal this type allows an item
  with known keys to be passed in to the save modal.
*/
// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface SaveConfirmationItem {}

type SaveConfirmationModalProps = {
  isOpen: boolean;
  onClose: (event?: React.MouseEvent<HTMLElement>) => void;
  onSave: (favMeta: BasicItem) => void;
  nameValidator: (name: string) => boolean;
  isSaving: boolean;
};

export const SaveConfirmationModal = (props: SaveConfirmationModalProps) => {
  const { t } = useTranslation(undefined, { i18n: i18nService });
  const favoriteText = t('favorite', { postProcess: 'capitalize' });
  const inputRef = React.useRef<HTMLInputElement>();
  const { onSave, onClose, isOpen, nameValidator, isSaving } = props;
  const [saveName, setSaveName] = useState<string | null>(null); // may need to inject props here
  const [nameValid, setNameValid] = useState<boolean>(false);

  useEffect(() => {
    // some weird async issue with the input ref :(
    const timeout = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!saveName) {
      setNameValid(false);
    } else {
      setNameValid(nameValidator(saveName));
    }
  }, [nameValidator, saveName]);

  return (
    <Dialog
      open={props.isOpen}
      onClose={(_event, reason) => {
        if (reason === 'escapeKeyDown') {
          onClose();
        }
      }}
      maxWidth={'md'}
      className={modal}
    >
      <DialogTitle classes={{ root: dialogTitle }}>
        <div>
          <span className="left-container">
            <i className="fas fa-save icon" />
            Save New {favoriteText}
          </span>
          <span className="right-container">
            <i className="far fa-times" onClick={onClose} />
          </span>
        </div>
      </DialogTitle>
      <DialogContent classes={{ root: dialogContent }}>
        <p className="instructions">{favoriteText} name:</p>
        <TextField
          id="outlined-basic"
          label="name"
          variant="outlined"
          error={!nameValid}
          inputRef={inputRef}
          onChange={(e) => setSaveName(e.target.value)}
          onKeyPress={(event) => {
            if (event.key === 'Enter') {
              if (saveName && nameValidator(saveName)) {
                onSave({ id: createId(), name: saveName });
              }
            }
          }}
        />
        <footer>
          <SquareActionButton
            disabled={!nameValid || isSaving}
            loading={isSaving}
            onClick={(_evt) => {
              if (saveName && nameValidator(saveName)) {
                onSave({ id: createId(), name: saveName });
              }
            }}
            text="Save"
            icon="far fa-check"
            type="primary"
          />
          <SquareActionButton onClick={onClose} text="Cancel" icon="fas fa-ban" type="secondary" disabled={isSaving} />
        </footer>
      </DialogContent>
    </Dialog>
  );
};
